import type { DiagnosisItem } from '@packages/types';
import { Popover, Tag, Typography } from 'antd';
import { useMemo } from 'react';

const { Text } = Typography;

interface DiagnosisPopoverContentProps {
    diagnoses: DiagnosisItem[];
    maxHeight?: number;
}

/**
 * Popover content showing full list of diagnoses with ICD codes and dates
 */
function DiagnosisPopoverContent({ diagnoses, maxHeight = 300 }: DiagnosisPopoverContentProps) {
    if (diagnoses.length === 0) {
        return (
            <div style={{ maxWidth: 320 }}>
                <Text type="secondary">No diagnoses recorded</Text>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: 360, maxHeight, overflowY: 'auto' }}>
            <div className="mb-2">
                <Text strong>Diagnoses ({diagnoses.length})</Text>
            </div>
            <div className="flex flex-col gap-2">
                {diagnoses.map((dx, index) => (
                    <div
                        key={`${dx.name}-${dx.start_date}-${index}`}
                        className="border-b border-gray-100 pb-2 last:border-0 last:pb-0"
                    >
                        <div className="flex items-start justify-between gap-2">
                            <Text className="text-sm font-medium text-gray-800">{dx.name}</Text>
                            {dx.icd_code && (
                                <Tag
                                    color="blue"
                                    className="shrink-0"
                                >
                                    {dx.icd_code}
                                </Tag>
                            )}
                        </div>
                        {dx.start_date && (
                            <Text
                                type="secondary"
                                className="text-xs"
                            >
                                First recorded:{' '}
                                {new Date(dx.start_date).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                })}
                            </Text>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

export interface DiagnosisListCellProps {
    /** Array of diagnoses sorted by date (newest first) */
    diagnoses: DiagnosisItem[];
    /** Maximum number of diagnoses to show in cell (default: 2) */
    maxDisplay?: number;
}

/**
 * Reusable table cell component for displaying diagnosis lists
 * - Shows truncated list in cell (e.g., "Diabetes, Hypertension +3 more")
 * - Hover popover shows full list with ICD codes and dates
 */
export function DiagnosisListCell({ diagnoses, maxDisplay = 2 }: DiagnosisListCellProps) {
    const displayText = useMemo(() => {
        if (!diagnoses || diagnoses.length === 0) {
            return null;
        }

        const displayItems = diagnoses.slice(0, maxDisplay);
        const remaining = diagnoses.length - maxDisplay;

        const text = displayItems.map(dx => dx.name).join(', ');

        if (remaining > 0) {
            return `${text} +${remaining} more`;
        }

        return text;
    }, [diagnoses, maxDisplay]);

    if (!diagnoses || diagnoses.length === 0) {
        return <span className="text-gray-400">—</span>;
    }

    return (
        <Popover
            content={<DiagnosisPopoverContent diagnoses={diagnoses} />}
            title={null}
            trigger="hover"
            placement="top"
            overlayStyle={{ maxWidth: 400 }}
        >
            <span className="cursor-help truncate text-gray-700">{displayText}</span>
        </Popover>
    );
}

export default DiagnosisListCell;
